import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Slider,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  RestartAlt as RestartAltIcon,
  ExpandMore as ExpandMoreIcon,
  ContactMail as ContactMailIcon,
  LocationOn as LocationOnIcon,
  Map as MapIcon,
  Delete as DeleteIcon,
  DynamicForm as DynamicFormIcon,
  Search as SearchIcon
} from '@mui/icons-material';
// Firebase imports removed - using SQL API now
// import { database } from '../../../firebase';
import { getContactSettings, saveContactSettings } from '../../../services/ecommerceApi';
import { useTranslation } from 'react-i18next';
import FormPickerDialog from './FormPickerDialog';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';
import RichTextEditor from '../../common/RichTextEditor';
import {
  readApiBool,
  readApiField,
  readApiNullableNumber,
  readApiNumber,
  readApiString,
} from '../utils/contactApiField';
import ContactTemplatePicker, {
  FORM_POSITION_LABELS,
  getTemplateDefaultLayout,
  getTemplateFormPositions,
  getTemplateMapPositions,
  MAP_POSITION_LABELS,
  resolveMapPositionForTemplate,
  type ContactTemplateType,
} from './ContactTemplatePicker';
import {
  getTemplateBackgroundColorOptions,
  getTemplateDefaultSectionBackground,
  templateHasBackgroundColorOptions,
} from './contactPageTemplateColors';

// Leaflet imports - statik import
import 'leaflet/dist/leaflet.css';
import * as LeafletModule from 'leaflet';

// Leaflet'i any olarak cast et (TypeScript hatalarını önlemek için)
const L = LeafletModule as any;

// Leaflet için marker icon düzeltmeleri
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

interface ContactSettings {
  templateType: ContactTemplateType;
  heroSection: {
    enabled: boolean;
    title: string;
    description: string;
    backgroundColor: string;
  };
  contactInfo: {
    enabled: boolean;
    address: string;
    phone: string;
    email: string;
    workingHours: string;
  };
  formSection: {
    enabled: boolean;
  };
  mapSettings: {
    enabled: boolean;
    latitude: number | null;
    longitude: number | null;
    zoom: number;
    height: number;
    width: number;
    title: string;
    /** 'osm' (OpenStreetMap, varsayılan) | 'google' (Google Maps embed) — SettingsJson.mapProvider'da saklanır */
    provider: 'osm' | 'google';
  };
  formSlug: string;
  formTitle?: string;
  chrome: {
    showHeader: boolean;
    showMenu: boolean;
    showFooter: boolean;
    showBottomNavbar: boolean;
  };
  layout: {
    formPosition: 'none' | 'drawer' | 'inline-left' | 'inline-right' | 'below' | 'above';
    mapPosition: 'none' | 'bottom-full' | 'right' | 'left' | 'top';
    useFormDrawer?: boolean;
    showOnHomePage?: boolean;
    homepageTabPosition?: 'top' | 'middle' | 'bottom';
  };
  design: {
    formTitle: string;
    formSubtitle: string;
    primaryColor: string;
    accentColor: string;
    accentGradientFrom: string;
    accentGradientTo: string;
    backgroundGradientFrom: string;
    backgroundGradientTo: string;
    sectionBackgroundColor: string;
    highlightText: string;
  };
}

interface MapSearchResult {
  displayName: string;
  latitude: number;
  longitude: number;
}

const defaultContactSettings: ContactSettings = {
  templateType: 'classic',
  heroSection: {
    enabled: true,
    title: 'Contact',
    description: 'Get in touch with us. We are happy to help you.',
    backgroundColor: '#ffffff'
  },
  contactInfo: {
    enabled: true,
    address: '',
    phone: '',
    email: '',
    workingHours: ''
  },
  formSection: {
    enabled: true
  },
  mapSettings: {
    enabled: false,
    latitude: null,
    longitude: null,
    zoom: 15,
    height: 400,
    width: 100,
    title: 'Konumumuz',
    provider: 'osm'
  },
  formSlug: '',
  formTitle: '',
  chrome: {
    showHeader: true,
    showMenu: true,
    showFooter: true,
    showBottomNavbar: true,
  },
  layout: {
    formPosition: 'below',
    mapPosition: 'bottom-full',
    useFormDrawer: false,
    showOnHomePage: false,
    homepageTabPosition: 'middle',
  },
  design: {
    formTitle: 'İletişim Formu',
    formSubtitle: 'Formu doldurarak bize hızlıca ulaşabilirsiniz.',
    primaryColor: '#dc2626',
    accentColor: '#2563eb',
    accentGradientFrom: '#2563eb',
    accentGradientTo: '#7c3aed',
    backgroundGradientFrom: '#eff6ff',
    backgroundGradientTo: '#faf5ff',
    sectionBackgroundColor: '#334155',
    highlightText: 'Her Zaman!',
  },
};

function ensureInlineFormLayout(settings: ContactSettings): ContactSettings {
  if (!settings.formSection.enabled || !settings.formSlug || settings.layout.formPosition !== 'none') {
    return settings;
  }
  const defaults = getTemplateDefaultLayout(settings.templateType);
  const formPosition = defaults.formPosition !== 'none' ? defaults.formPosition : 'below';
  return {
    ...settings,
    layout: { ...settings.layout, formPosition },
  };
}

function parseContactSettingsJson(raw: unknown): Partial<Pick<ContactSettings, 'chrome' | 'layout' | 'design'>> & {
  templateType?: ContactTemplateType;
  mapProvider?: 'osm' | 'google';
} {
  if (!raw) return {};
  let parsed: Record<string, unknown> = {};
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return {};
    }
  } else if (typeof raw === 'object') {
    parsed = raw as Record<string, unknown>;
  }

  const jsonTemplate = parsed.templateType ?? parsed.TemplateType;
  const validTemplates: ContactTemplateType[] = [
    'classic',
    'cards-form-split',
    'form-info-map-stack',
    'info-map-split',
    'gradient-form',
    'form-channels',
    'cards-map-stack',
    'channel-cards',
  ];
  const templateType = validTemplates.includes(jsonTemplate as ContactTemplateType)
    ? (jsonTemplate as ContactTemplateType)
    : undefined;

  return {
    chrome: (parsed.chrome ?? {}) as ContactSettings['chrome'],
    layout: (parsed.layout ?? {}) as ContactSettings['layout'],
    design: (parsed.design ?? {}) as ContactSettings['design'],
    templateType,
    // Alan yoksa 'osm': mapProvider bilmeyen mevcut kayıtlar OSM'de kalır (geriye uyumluluk)
    mapProvider: parsed.mapProvider === 'google' ? 'google' : 'osm',
  };
}

function resolveTemplateTypeFromApi(
  data: Record<string, unknown>,
  jsonTemplateType?: ContactTemplateType
): ContactTemplateType {
  const validTemplates: ContactTemplateType[] = [
    'classic',
    'cards-form-split',
    'form-info-map-stack',
    'info-map-split',
    'gradient-form',
    'form-channels',
    'cards-map-stack',
    'channel-cards',
  ];
  const candidates = [data.templateType, data.TemplateType, jsonTemplateType];
  for (const candidate of candidates) {
    if (validTemplates.includes(candidate as ContactTemplateType)) {
      return candidate as ContactTemplateType;
    }
  }
  return 'classic';
}

function buildSettingsFromApi(data: Record<string, unknown>): ContactSettings {
  const jsonParts = parseContactSettingsJson(readApiField(data, 'settingsJson'));
  return {
    templateType: resolveTemplateTypeFromApi(data, jsonParts.templateType),
    heroSection: {
      enabled: readApiBool(data, 'heroEnabled'),
      title: readApiString(data, 'heroTitle'),
      description: readApiString(data, 'heroDescription'),
      backgroundColor: readApiString(data, 'heroBackgroundColor', '#ffffff'),
    },
    contactInfo: {
      enabled: readApiBool(data, 'contactInfoEnabled'),
      address: readApiString(data, 'contactAddress'),
      phone: readApiString(data, 'contactPhone'),
      email: readApiString(data, 'contactEmail'),
      workingHours: readApiString(data, 'contactWorkingHours'),
    },
    formSection: {
      enabled: readApiBool(data, 'formEnabled'),
    },
    mapSettings: {
      enabled: Boolean(readApiField(data, 'mapEnabled')),
      latitude: readApiNullableNumber(data, 'mapLatitude'),
      longitude: readApiNullableNumber(data, 'mapLongitude'),
      zoom: readApiNumber(data, 'mapZoom', 15),
      height: readApiNumber(data, 'mapHeight', 400),
      width: Math.max(40, Math.min(100, readApiNumber(data, 'mapWidth', 100))),
      title: readApiString(data, 'mapTitle', 'Konumumuz'),
      provider: jsonParts.mapProvider ?? 'osm',
    },
    formSlug: readApiString(data, 'formSlug'),
    formTitle: jsonParts.design?.formTitle || '',
    chrome: { ...defaultContactSettings.chrome, ...jsonParts.chrome },
    layout: { ...defaultContactSettings.layout, ...jsonParts.layout },
    design: { ...defaultContactSettings.design, ...jsonParts.design },
  };
}

interface ContactSettingsComponentProps {
  companyId: string | null;
  configId: string; // ✅ SQL için configId eklendi
  settings?: ContactSettings | null;
  onSettingsChange?: (settings: ContactSettings) => void;
}

const ContactSettingsComponent: React.FC<ContactSettingsComponentProps> = ({ 
  companyId,
  configId, // ✅ SQL için configId
  settings: externalSettings, 
  onSettingsChange 
}) => {
  const { t } = useTranslation('salesSystem');
  const [settings, setSettings] = useState<ContactSettings>(defaultContactSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mapAccordionOpen, setMapAccordionOpen] = useState(false);
  const [formPickerOpen, setFormPickerOpen] = useState(false);
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [mapSearchLoading, setMapSearchLoading] = useState(false);
  const [mapSearchResults, setMapSearchResults] = useState<MapSearchResult[]>([]);
  const [mapSearchError, setMapSearchError] = useState<string | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const withMapEnabled = (latitude: number, longitude: number) => {
    const current = settingsRef.current;
    return {
      ...current,
      layout: {
        ...current.layout,
        mapPosition: resolveMapPositionForTemplate(
          current.templateType,
          current.layout.mapPosition,
          true
        ),
      },
      mapSettings: {
        ...current.mapSettings,
        latitude,
        longitude,
        enabled: true,
      },
    };
  };

  // Ayarları yükle (SQL'den)
  useEffect(() => {
    const loadSettings = async () => {
      if (!companyId || !configId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('🔄 [CONTACT] SQL den yükleniyor, ConfigId:', configId);
        
        // Önce external settings'i kontrol et
        if (externalSettings) {
          setSettings(externalSettings);
          setLoading(false);
          return;
        }

        // ✅ SQL API'den yükle
        const response = await getContactSettings(companyId, configId); // ✅ companyId eklendi
        console.log('📦 [CONTACT] Backend response:', response);
        if (response && response.success && response.data) {
          console.log('🗺️ [CONTACT] Harita verileri:', {
            latitude: response.data.mapLatitude,
            longitude: response.data.mapLongitude,
            enabled: response.data.mapEnabled
          });
          const loadedSettings = buildSettingsFromApi(response.data as Record<string, unknown>);
          console.log('🎯 [CONTACT] Yüklenen settings:', loadedSettings);
          setSettings(loadedSettings);
          if (onSettingsChange) {
            onSettingsChange(loadedSettings);
          }
          console.log('✅ [CONTACT] SQL den yükleme başarılı');
        } else {
          setSettings(defaultContactSettings);
          if (onSettingsChange) {
            onSettingsChange(defaultContactSettings);
          }
        }
      } catch (err) {
        console.error('❌ [CONTACT] İletişim ayarları yüklenirken hata:', err);
        setError(t('ecommerceContactSettings.messages.loadError'));
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [companyId, configId, t]); // ✅ onSettingsChange ve externalSettings kaldırıldı (sonsuz döngü önlendi)

  // Harita başlatma
  useEffect(() => {
    const initMap = () => {
      if (!mapRef.current || mapInstanceRef.current) return;

      try {
        // Harita container'ının boyutunu kontrol et
        const container = mapRef.current;
        if (container.offsetWidth === 0 || container.offsetHeight === 0) {
          console.warn('Harita container boyutu 0, harita yüklenemedi');
          return;
        }

        // Harita oluştur - eğer mevcut konum varsa oraya odaklan
        const initialLat = settings.mapSettings.latitude || 39.9334;
        const initialLng = settings.mapSettings.longitude || 32.8597;
        const initialZoom = settings.mapSettings.latitude ? settings.mapSettings.zoom : 6;
        
        console.log('🗺️ [CONTACT] Harita başlatılıyor:', { lat: initialLat, lng: initialLng, zoom: initialZoom });
        const map = L.map(container).setView([initialLat, initialLng], initialZoom);

        // Tile layer ekle
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19
        }).addTo(map);

        mapInstanceRef.current = map;

        // ✅ Eğer mevcut konum varsa marker ekle
        if (settings.mapSettings.latitude && settings.mapSettings.longitude) {
          const marker = L.marker([settings.mapSettings.latitude, settings.mapSettings.longitude]).addTo(map);
          markerRef.current = marker;
          console.log('📍 [CONTACT] Mevcut konum marker eklendi:', { 
            lat: settings.mapSettings.latitude, 
            lng: settings.mapSettings.longitude 
          });
        }

        // Harita boyutunu yeniden hesapla
        setTimeout(() => {
          map.invalidateSize();
        }, 100);

        // Harita tıklama olayı
        map.on('click', (e: any) => {
          const { lat, lng } = e.latlng;
          
          // Mevcut marker'ı kaldır
          if (markerRef.current) {
            map.removeLayer(markerRef.current);
          }

          // Yeni marker ekle
          const marker = L.marker([lat, lng]).addTo(map);
          markerRef.current = marker;

          const newSettings = withMapEnabled(lat, lng);
          setSettings(newSettings);
          if (onSettingsChange) {
            onSettingsChange(newSettings);
          }

          // ✅ Auto-save tetikle
          debouncedAutoSave(newSettings);

          setSuccess(t('ecommerceContactSettings.messages.locationSelected'));
          setTimeout(() => setSuccess(null), 3000);
        });
      } catch (error) {
        console.error('Harita yüklenirken hata:', error);
      }
    };

    // Harita initialization'ını biraz geciktir (DOM render sonrası)
    const timeoutId = setTimeout(() => {
      initMap();
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [loading]); // loading bitince haritayı başlat

  // Mevcut konumu haritada göster
  useEffect(() => {
    const updateMapLocation = () => {
      console.log('🔄 [CONTACT MAP] Marker güncelleme çağrıldı:', {
        hasMap: !!mapInstanceRef.current,
        lat: settings.mapSettings.latitude,
        lng: settings.mapSettings.longitude
      });

      if (!mapInstanceRef.current || !settings.mapSettings.latitude || !settings.mapSettings.longitude) {
        console.log('⏭️ [CONTACT MAP] Marker güncellenemedi - harita yok veya konum yok');
        return;
      }

      try {
        const map = mapInstanceRef.current;
        const { latitude, longitude, zoom } = settings.mapSettings;

        // Haritayı konuma odakla
        map.setView([latitude, longitude], zoom);

        // Mevcut marker'ı kaldır
        if (markerRef.current) {
          map.removeLayer(markerRef.current);
        }

        // Yeni marker ekle
        const marker = L.marker([latitude, longitude]).addTo(map);
        markerRef.current = marker;
        console.log('✅ [CONTACT MAP] Marker eklendi:', { lat: latitude, lng: longitude });
      } catch (error) {
        console.error('Harita konumu güncellenirken hata:', error);
      }
    };

    updateMapLocation();
  }, [settings.mapSettings.latitude, settings.mapSettings.longitude, settings.mapSettings.zoom]);

  // Auto-save fonksiyonu (debounced)
  const autoSave = async (newSettings: ContactSettings) => {
    if (!companyId || !configId) {
      console.log('⏭️ [CONTACT AUTO-SAVE] CompanyId veya ConfigId yok, kayıt atlanıyor');
      return;
    }

    try {
      console.log('💾 [CONTACT AUTO-SAVE] Otomatik kaydediliyor...');

      const requestData = {
        companyId: companyId,
        configId: configId,
        heroEnabled: newSettings.heroSection.enabled,
        contactInfoEnabled: newSettings.contactInfo.enabled,
        formEnabled: newSettings.formSection.enabled,
        heroTitle: newSettings.heroSection.title,
        heroDescription: newSettings.heroSection.description,
        heroBackgroundColor: newSettings.heroSection.backgroundColor,
        contactAddress: newSettings.contactInfo.address,
        contactPhone: newSettings.contactInfo.phone,
        contactEmail: newSettings.contactInfo.email,
        contactWorkingHours: newSettings.contactInfo.workingHours,
        mapEnabled: newSettings.mapSettings.enabled,
        mapLatitude: newSettings.mapSettings.latitude,
        mapLongitude: newSettings.mapSettings.longitude,
        mapZoom: newSettings.mapSettings.zoom,
        mapHeight: newSettings.mapSettings.height,
        mapWidth: newSettings.mapSettings.width,
        mapTitle: newSettings.mapSettings.title,
        formSlug: newSettings.formSlug || null,
        templateType: newSettings.templateType,
        settingsJson: JSON.stringify({
          templateType: newSettings.templateType,
          chrome: newSettings.chrome,
          layout: newSettings.layout,
          design: newSettings.design,
          mapProvider: newSettings.mapSettings.provider,
        }),
      };
      
      await saveContactSettings(requestData);
      console.log('✅ [CONTACT AUTO-SAVE] Otomatik kayıt başarılı');
      notifyEcommerceSaved(t('ecommerceGeneral.autoSave.savedToast'));
    } catch (err) {
      console.error('❌ [CONTACT AUTO-SAVE] Otomatik kayıt hatası:', err);
    }
  };

  // Debounced auto-save
  const debouncedAutoSave = (newSettings: ContactSettings) => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      autoSave(newSettings);
    }, 1000); // 1 saniye bekle
  };

  // Ayarları kaydet (SQL'e) - Manuel kayıt butonu için
  // ❌ handleSave kaldırıldı - artık auto-save kullanıyoruz

  // Varsayılan ayarlara sıfırla
  const handleReset = () => {
    setSettings(defaultContactSettings);
    setSuccess(t('ecommerceContactSettings.messages.resetSuccess'));
    setTimeout(() => setSuccess(null), 3000);

    // Haritadaki marker'ı kaldır
    if (markerRef.current && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(markerRef.current);
      markerRef.current = null;
    }
  };

  // Konumu temizle
  const handleClearLocation = () => {
    setSettings(prev => ({
      ...prev,
      mapSettings: {
        ...prev.mapSettings,
        latitude: null,
        longitude: null,
        enabled: false
      }
    }));

    // Haritadaki marker'ı kaldır
    if (markerRef.current && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(markerRef.current);
      markerRef.current = null;
    }

    setSuccess(t('ecommerceContactSettings.messages.locationCleared'));
    setTimeout(() => setSuccess(null), 3000);
  };

  const applyMapLocation = (latitude: number, longitude: number, zoom?: number) => {
    const targetZoom = Number.isFinite(zoom) ? Number(zoom) : Math.max(14, settings.mapSettings.zoom || 15);

    const newSettings: ContactSettings = {
      ...settings,
      mapSettings: {
        ...settings.mapSettings,
        latitude,
        longitude,
        zoom: targetZoom,
        enabled: true
      }
    };

    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    debouncedAutoSave(newSettings);

    if (mapInstanceRef.current) {
      const map = mapInstanceRef.current;
      map.setView([latitude, longitude], targetZoom);
      if (markerRef.current) {
        map.removeLayer(markerRef.current);
      }
      markerRef.current = L.marker([latitude, longitude]).addTo(map);
    }
  };

  const handleMapSearch = async () => {
    const query = mapSearchQuery.trim();
    if (!query) {
      setMapSearchResults([]);
      setMapSearchError('Lütfen aramak istediğiniz adresi yazın.');
      return;
    }

    try {
      setMapSearchLoading(true);
      setMapSearchError(null);
      setMapSearchResults([]);

      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=5&addressdetails=1&accept-language=tr&q=${encodeURIComponent(query)}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const normalized: MapSearchResult[] = Array.isArray(data)
        ? data
            .map((item: any) => ({
              displayName: String(item?.display_name || '').trim(),
              latitude: Number(item?.lat),
              longitude: Number(item?.lon),
            }))
            .filter((item: MapSearchResult) =>
              item.displayName &&
              Number.isFinite(item.latitude) &&
              Number.isFinite(item.longitude)
            )
        : [];

      if (normalized.length === 0) {
        setMapSearchError('Adres bulunamadı. Daha detaylı bir arama deneyin.');
        return;
      }

      setMapSearchResults(normalized);
      // Kullanıcı hızlı aksın diye ilk sonucu direkt uygula.
      applyMapLocation(normalized[0].latitude, normalized[0].longitude, 16);
      setSuccess('Adres bulundu, harita konumu güncellendi.');
      setTimeout(() => setSuccess(null), 2500);
    } catch (err) {
      console.error('❌ [CONTACT MAP] Adres arama hatası:', err);
      setMapSearchError('Adres araması sırasında bir hata oluştu.');
    } finally {
      setMapSearchLoading(false);
    }
  };

  // Hero section güncelle (text ve boolean alanlar)
  const updateHeroSection = <K extends keyof typeof settings.heroSection>(
    field: K,
    value: typeof settings.heroSection[K]
  ) => {
    const newSettings = {
      ...settings,
      heroSection: {
        ...settings.heroSection,
        [field]: value
      }
    };
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    // ✅ Auto-save tetikle
    debouncedAutoSave(newSettings);
  };

  // Contact info güncelle (text ve boolean alanlar)
  const updateContactInfo = <K extends keyof typeof settings.contactInfo>(
    field: K,
    value: typeof settings.contactInfo[K]
  ) => {
    const newSettings = {
      ...settings,
      contactInfo: {
        ...settings.contactInfo,
        [field]: value
      }
    };
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    // ✅ Auto-save tetikle
    debouncedAutoSave(newSettings);
  };

  // Form bölümü göster/gizle anahtarı
  const updateFormSectionEnabled = (enabled: boolean) => {
    const newSettings = ensureInlineFormLayout({
      ...settings,
      formSection: {
        ...settings.formSection,
        enabled,
      },
    });
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    debouncedAutoSave(newSettings);
  };

  const applySettings = (newSettings: ContactSettings) => {
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    debouncedAutoSave(newSettings);
  };

  const updateChrome = (key: keyof ContactSettings['chrome'], value: boolean) => {
    applySettings({
      ...settings,
      chrome: { ...settings.chrome, [key]: value },
    });
  };

  const updateLayout = (
    key: keyof ContactSettings['layout'],
    value: ContactSettings['layout'][typeof key]
  ) => {
    applySettings({
      ...settings,
      layout: { ...settings.layout, [key]: value },
    });
  };

  const isFormDrawerActive =
    settings.layout.useFormDrawer === true || settings.layout.formPosition === 'drawer';

  const updateDesign = (key: keyof ContactSettings['design'], value: string) => {
    applySettings({
      ...settings,
      design: { ...settings.design, [key]: value },
    });
  };

  const updateTemplateType = (templateType: ContactTemplateType) => {
    const defaults = getTemplateDefaultLayout(templateType);
    applySettings({
      ...settings,
      templateType,
      layout: {
        ...defaults,
        mapPosition: settings.mapSettings.enabled
          ? resolveMapPositionForTemplate(templateType, settings.layout.mapPosition, true)
          : defaults.mapPosition,
        showOnHomePage: settings.layout.showOnHomePage === true,
        homepageTabPosition: settings.layout.homepageTabPosition || 'middle',
      },
      design: {
        ...settings.design,
        sectionBackgroundColor: getTemplateDefaultSectionBackground(templateType),
      },
    });
  };

  // Map settings güncelle
  const updateMapSettings = (field: keyof typeof settings.mapSettings, value: any) => {
    const enablingMap = field === 'enabled' && value === true;
    const newSettings = {
      ...settings,
      layout: enablingMap
        ? {
            ...settings.layout,
            mapPosition: resolveMapPositionForTemplate(
              settings.templateType,
              settings.layout.mapPosition,
              true
            ),
          }
        : settings.layout,
      mapSettings: {
        ...settings.mapSettings,
        [field]: value
      }
    };
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    // ✅ Auto-save tetikle
    debouncedAutoSave(newSettings);
  };

  const updateFormSelection = (slug: string, title?: string) => {
    const newSettings = ensureInlineFormLayout({
      ...settings,
      formSlug: slug || '',
      formTitle: title || '',
      design: {
        ...settings.design,
        formTitle: title || settings.design.formTitle,
      },
    });
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    debouncedAutoSave(newSettings);
    setSuccess(slug ? 'İletişim formu seçildi ve kaydedildi.' : 'İletişim formu kaldırıldı.');
    setTimeout(() => setSuccess(null), 2500);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const allowedFormPositions = getTemplateFormPositions(settings.templateType);
  const allowedMapPositions = getTemplateMapPositions(settings.templateType);

  const renderColorField = (
    label: string,
    key: keyof ContactSettings['design'],
    disabled = false,
    helperText?: string
  ) => (
    <TextField
      fullWidth
      label={label}
      value={settings.design[key]}
      onChange={(e) => updateDesign(key, e.target.value)}
      variant="outlined"
      disabled={disabled}
      helperText={helperText}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Box
              component="input"
              type="color"
              value={settings.design[key]}
              onChange={(e) => updateDesign(key, e.target.value)}
              sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
              disabled={disabled}
            />
          </InputAdornment>
        ),
      }}
    />
  );

  const renderHeroColorField = (label: string, helperText?: string) => (
    <TextField
      fullWidth
      label={label}
      value={settings.heroSection.backgroundColor}
      onChange={(e) => updateHeroSection('backgroundColor', e.target.value)}
      variant="outlined"
      helperText={helperText}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Box
              component="input"
              type="color"
              value={settings.heroSection.backgroundColor}
              onChange={(e) => updateHeroSection('backgroundColor', e.target.value)}
              sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
            />
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight="bold">
          <ContactMailIcon sx={{ mr: 1.5, color: 'primary.main' }} />
          {t('ecommerceContactSettings.title')}
        </Typography>
        <Tooltip title={t('ecommerceAboutSettings.actions.resetToDefaultSettings')}>
          <IconButton onClick={handleReset} color="secondary">
            <RestartAltIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Template & Layout */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <MapIcon sx={{ mr: 1, color: 'primary.main' }} />
            Şablon & Görünüm
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Şablon Seçimi
              </Typography>
              <ContactTemplatePicker
                value={settings.templateType}
                onChange={updateTemplateType}
              />
            </Grid>

            {templateHasBackgroundColorOptions(settings.templateType) && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 1 }}>
                    Şablon Arka Plan Renkleri
                  </Typography>
                </Grid>
                {getTemplateBackgroundColorOptions(settings.templateType).map((option) => (
                  <Grid item xs={12} sm={6} md={4} key={`${option.source}-${option.key}`}>
                    {option.source === 'design'
                      ? renderColorField(option.label, option.key, false, option.helperText)
                      : renderHeroColorField(
                          option.label,
                          settings.heroSection.enabled ? option.helperText : 'Hero kapalıyken görünmez'
                        )}
                  </Grid>
                ))}
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 1 }}>
                Menü & Chrome
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.chrome.showHeader}
                    onChange={(e) => updateChrome('showHeader', e.target.checked)}
                  />
                }
                label="Header Göster"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.chrome.showMenu}
                    onChange={(e) => updateChrome('showMenu', e.target.checked)}
                  />
                }
                label="Menü Göster"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.chrome.showFooter}
                    onChange={(e) => updateChrome('showFooter', e.target.checked)}
                  />
                }
                label="Footer Göster"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.chrome.showBottomNavbar}
                    onChange={(e) => updateChrome('showBottomNavbar', e.target.checked)}
                  />
                }
                label="Alt Navbar Göster"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 1 }}>
                Konum Ayarları
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Form Konumu</InputLabel>
                <Select
                  value={
                    allowedFormPositions.includes(settings.layout.formPosition)
                      ? settings.layout.formPosition
                      : allowedFormPositions[0]
                  }
                  label="Form Konumu"
                  onChange={(e) => updateLayout('formPosition', e.target.value)}
                  disabled={!settings.formSection.enabled}
                >
                  {allowedFormPositions.map((pos) => (
                    <MenuItem key={pos} value={pos}>
                      {FORM_POSITION_LABELS[pos]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Harita Konumu</InputLabel>
                <Select
                  value={
                    allowedMapPositions.includes(settings.layout.mapPosition)
                      ? settings.layout.mapPosition
                      : allowedMapPositions[0]
                  }
                  label="Harita Konumu"
                  onChange={(e) => updateLayout('mapPosition', e.target.value)}
                  disabled={!settings.mapSettings.enabled}
                >
                  {allowedMapPositions.map((pos) => (
                    <MenuItem key={pos} value={pos}>
                      {MAP_POSITION_LABELS[pos]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 1 }}>
                Tasarım Renkleri
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Form butonu, harita çerçevesi ve Form &amp; Harita bilgi kartı Genel Ayarlar&apos;daki site rengini kullanır. Aşağıdaki alanlar ek vurgu içindir; sitede genel renk önceliklidir.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              {renderColorField('Vurgu Rengi', 'accentColor')}
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Vurgulu Metin (Gradient şablon)"
                value={settings.design.highlightText}
                onChange={(e) => updateDesign('highlightText', e.target.value)}
                variant="outlined"
                placeholder="Her Zaman!"
                helperText="Başlıkta renkli gösterilecek kelime"
                disabled={settings.templateType !== 'gradient-form'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderColorField('Vurgu Gradient — Başlangıç', 'accentGradientFrom')}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderColorField('Vurgu Gradient — Bitiş', 'accentGradientTo')}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Hero Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <ContactMailIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceContactSettings.sections.hero')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.heroSection.enabled}
                    onChange={(e) => updateHeroSection('enabled', e.target.checked)}
                  />
                }
                label="Hero Bölümünü Göster"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.fields.title')}
                value={settings.heroSection.title}
                onChange={(e) => updateHeroSection('title', e.target.value)}
                variant="outlined"
                disabled={!settings.heroSection.enabled}
              />
            </Grid>
            <Grid item xs={12}>
              <RichTextEditor
                label={t('ecommerceAboutSettings.fields.description')}
                value={settings.heroSection.description}
                onChange={(html) => updateHeroSection('description', html)}
                placeholder="Açıklama yazın. Kalın, madde, görsel ve hizalama araçlarını kullanabilirsiniz."
                minHeight={160}
                disabled={!settings.heroSection.enabled}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.fields.backgroundColor')}
                value={settings.heroSection.backgroundColor}
                onChange={(e) => updateHeroSection('backgroundColor', e.target.value)}
                variant="outlined"
                disabled={!settings.heroSection.enabled}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={settings.heroSection.backgroundColor}
                        onChange={(e) => updateHeroSection('backgroundColor', e.target.value)}
                        sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                        disabled={!settings.heroSection.enabled}
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Contact Information */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceContactSettings.sections.contactInformation')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {t('ecommerceContactSettings.helpers.alsoShownInFooter')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.contactInfo.enabled}
                    onChange={(e) => updateContactInfo('enabled', e.target.checked)}
                  />
                }
                label="İletişim Bilgileri Bölümünü Göster"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('ecommerceContactSettings.fields.address')}
                value={settings.contactInfo.address}
                onChange={(e) => updateContactInfo('address', e.target.value)}
                variant="outlined"
                placeholder={t('ecommerceContactSettings.placeholders.address')}
                disabled={!settings.contactInfo.enabled}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('ecommerceContactSettings.fields.phone')}
                value={settings.contactInfo.phone}
                onChange={(e) => updateContactInfo('phone', e.target.value)}
                variant="outlined"
                placeholder="+90 (555) 123 45 67"
                disabled={!settings.contactInfo.enabled}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('ecommerceContactSettings.fields.email')}
                type="email"
                value={settings.contactInfo.email}
                onChange={(e) => updateContactInfo('email', e.target.value)}
                variant="outlined"
                placeholder="info@company.com"
                disabled={!settings.contactInfo.enabled}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('ecommerceContactSettings.fields.workingHours')}
                value={settings.contactInfo.workingHours}
                onChange={(e) => updateContactInfo('workingHours', e.target.value)}
                variant="outlined"
                placeholder={t('ecommerceContactSettings.placeholders.workingHours')}
                disabled={!settings.contactInfo.enabled}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Form Settings */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <DynamicFormIcon sx={{ mr: 1, color: 'primary.main' }} />
            İletişim Formu
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.formSection.enabled}
                    onChange={(e) => updateFormSectionEnabled(e.target.checked)}
                  />
                }
                label="İletişim Formu Bölümünü Göster"
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                İÇERİK → Formlar sekmesinde oluşturduğun bir formu buradan seçebilirsin. Form konumuna göre sayfa içinde veya yandan açılır panel olarak gösterilir.
              </Alert>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Form Başlığı"
                value={settings.design.formTitle}
                onChange={(e) => updateDesign('formTitle', e.target.value)}
                variant="outlined"
                disabled={!settings.formSection.enabled}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Form Alt Başlığı"
                value={settings.design.formSubtitle}
                onChange={(e) => updateDesign('formSubtitle', e.target.value)}
                variant="outlined"
                disabled={!settings.formSection.enabled}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 1 }}>
                Yan Form Paneli (Drawer)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Formu şablona gömülü yerine sağ yan panelde göstermek için anahtarı açın. Kartlar &amp; Harita gibi kompakt şablonlarda önerilir.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.layout.useFormDrawer === true}
                    onChange={(e) => updateLayout('useFormDrawer', e.target.checked)}
                    disabled={!settings.formSection.enabled || !settings.formSlug}
                  />
                }
                label="Formu yan panelde göster"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5, mt: -0.5 }}>
                {settings.layout.useFormDrawer
                  ? 'Form sayfa içinde değil, sağdan açılır panelde gelir.'
                  : 'Form, şablon ve form konumu ayarına göre sayfa içinde gösterilir.'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.layout.showOnHomePage === true}
                    onChange={(e) => updateLayout('showOnHomePage', e.target.checked)}
                    disabled={!settings.formSection.enabled || !settings.formSlug}
                  />
                }
                label="Anasayfada da göster"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5, mt: -0.5 }}>
                Açıkken aynı form vitrin anasayfasında sağ kenar şeridi olarak görünür.
              </Typography>
            </Grid>
            {settings.layout.showOnHomePage && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!settings.formSection.enabled || !settings.formSlug}>
                  <InputLabel>Anasayfa konumu</InputLabel>
                  <Select
                    label="Anasayfa konumu"
                    value={settings.layout.homepageTabPosition || 'middle'}
                    onChange={(e) =>
                      updateLayout(
                        'homepageTabPosition',
                        e.target.value as 'top' | 'middle' | 'bottom'
                      )
                    }
                  >
                    <MenuItem value="top">Üst</MenuItem>
                    <MenuItem value="middle">Orta</MenuItem>
                    <MenuItem value="bottom">Alt</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Drawer Sekme Rengi"
                value={settings.design.primaryColor}
                onChange={(e) => updateDesign('primaryColor', e.target.value)}
                variant="outlined"
                disabled={!settings.formSection.enabled}
                helperText={
                  isFormDrawerActive || settings.layout.showOnHomePage
                    ? 'İletişim sayfası ve anasayfa kenar şeridinin rengi'
                    : 'Yan panel veya anasayfada göster açıkken kenar şeridine uygulanır'
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={settings.design.primaryColor}
                        onChange={(e) => updateDesign('primaryColor', e.target.value)}
                        sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                        disabled={!settings.formSection.enabled}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Seçilen Form Slug"
                value={settings.formSlug || ''}
                variant="outlined"
                InputProps={{ readOnly: true }}
                helperText={settings.formSlug ? 'Bu form storefront iletişim sayfasında gösterilecek.' : 'Henüz form seçilmedi.'}
                disabled={!settings.formSection.enabled}
              />
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => setFormPickerOpen(true)}
                startIcon={<DynamicFormIcon />}
                disabled={!settings.formSection.enabled}
              >
                Form Seç
              </Button>
              <Button
                variant="outlined"
                color="error"
                disabled={!settings.formSlug || !settings.formSection.enabled}
                onClick={() => updateFormSelection('', '')}
              >
                Temizle
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>



      {/* Map Settings */}
      <Accordion 
        expanded={mapAccordionOpen}
        onChange={(event, isExpanded) => {
          setMapAccordionOpen(isExpanded);
          if (isExpanded) {
            // Accordion açıldığında haritayı yeniden başlat
            setTimeout(() => {
              if (mapInstanceRef.current && mapRef.current) {
                try {
                  mapInstanceRef.current.invalidateSize();
                } catch (error) {
                  console.warn('Harita invalidateSize hatası:', error);
                }
              } else {
                // Harita yoksa yeniden oluştur
                if (mapRef.current && !mapInstanceRef.current) {
                  try {
                    const map = L.map(mapRef.current).setView([39.9334, 32.8597], 6);
                    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                      attribution: '© OpenStreetMap contributors',
                      maxZoom: 19
                    }).addTo(map);
                    mapInstanceRef.current = map;

                    // Harita tıklama olayı
                    map.on('click', (e: any) => {
                      const { lat, lng } = e.latlng;
                      
                      if (markerRef.current) {
                        map.removeLayer(markerRef.current);
                      }

                      const marker = L.marker([lat, lng]).addTo(map);
                      markerRef.current = marker;

                      const newSettings = withMapEnabled(lat, lng);
                      setSettings(newSettings);
                      if (onSettingsChange) {
                        onSettingsChange(newSettings);
                      }

                      // ✅ Auto-save tetikle
                      debouncedAutoSave(newSettings);

                      setSuccess(t('ecommerceContactSettings.messages.locationSelected'));
                      setTimeout(() => setSuccess(null), 3000);
                    });

                    // Mevcut konumu göster
                    if (settings.mapSettings.latitude && settings.mapSettings.longitude) {
                      map.setView([settings.mapSettings.latitude, settings.mapSettings.longitude], settings.mapSettings.zoom);
                      const marker = L.marker([settings.mapSettings.latitude, settings.mapSettings.longitude]).addTo(map);
                      markerRef.current = marker;
                    }
                  } catch (error) {
                    console.error('Harita oluşturulurken hata:', error);
                  }
                }
              }
            }, 300);
          }
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <MapIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceContactSettings.sections.mapSettings')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.mapSettings.enabled}
                      onChange={(e) => updateMapSettings('enabled', e.target.checked)}
                    />
                  }
                  label={t('ecommerceContactSettings.map.showMap')}
                />
                {settings.mapSettings.latitude && settings.mapSettings.longitude && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={handleClearLocation}
                  >
                    {t('ecommerceContactSettings.map.clearLocation')}
                  </Button>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth disabled={!settings.mapSettings.enabled}>
                <InputLabel>Harita Sağlayıcısı</InputLabel>
                <Select
                  value={settings.mapSettings.provider}
                  label="Harita Sağlayıcısı"
                  onChange={(e) => updateMapSettings('provider', e.target.value as 'osm' | 'google')}
                >
                  <MenuItem value="osm">OpenStreetMap</MenuItem>
                  <MenuItem value="google">Google Haritalar</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                Sitenizdeki iletişim haritasında hangi servisin görüneceğini belirler. OpenStreetMap verisi
                bölgenizde güncel değilse (eski sokak adları vb.) Google Haritalar'ı seçebilirsiniz.
                Konum seçimi her iki durumda da aşağıdaki haritadan yapılır; koordinatlar ortaktır.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('ecommerceContactSettings.map.mapTitle')}
                value={settings.mapSettings.title}
                onChange={(e) => updateMapSettings('title', e.target.value)}
                variant="outlined"
                disabled={!settings.mapSettings.enabled}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Adres ara (örn: Kadıköy, İstanbul)"
                  value={mapSearchQuery}
                  onChange={(e) => setMapSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (!mapSearchLoading && settings.mapSettings.enabled) {
                        handleMapSearch();
                      }
                    }
                  }}
                  disabled={!settings.mapSettings.enabled || mapSearchLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleMapSearch}
                  disabled={!settings.mapSettings.enabled || mapSearchLoading}
                  sx={{ minWidth: 90, height: 40 }}
                >
                  {mapSearchLoading ? 'Aranıyor' : 'Ara'}
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                Adres yazarak konumu otomatik bulabilirsiniz.
              </Typography>
            </Grid>

            {mapSearchError && (
              <Grid item xs={12}>
                <Alert severity="warning">{mapSearchError}</Alert>
              </Grid>
            )}

            {mapSearchResults.length > 1 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                  Alternatif Sonuçlar
                </Typography>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    maxHeight: 220,
                    overflowY: 'auto'
                  }}
                >
                  {mapSearchResults.map((result, index) => (
                    <Button
                      key={`${result.latitude}_${result.longitude}_${index}`}
                      variant="text"
                      sx={{
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        textAlign: 'left',
                        borderRadius: 0,
                        px: 1.5,
                        py: 1,
                        fontSize: '0.82rem',
                        borderBottom: index === mapSearchResults.length - 1 ? 'none' : '1px solid',
                        borderColor: 'divider'
                      }}
                      onClick={() => applyMapLocation(result.latitude, result.longitude, 16)}
                      disabled={!settings.mapSettings.enabled}
                    >
                      {result.displayName}
                    </Button>
                  ))}
                </Box>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Typography gutterBottom>{t('ecommerceContactSettings.map.mapHeight')}</Typography>
              <Slider
                value={settings.mapSettings.height}
                onChange={(e, newValue) => updateMapSettings('height', newValue as number)}
                min={200}
                max={800}
                step={50}
                valueLabelDisplay="auto"
                disabled={!settings.mapSettings.enabled}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography gutterBottom>Harita Genişliği (%)</Typography>
              <Slider
                value={settings.mapSettings.width}
                onChange={(e, newValue) => updateMapSettings('width', newValue as number)}
                min={40}
                max={100}
                step={5}
                valueLabelDisplay="auto"
                disabled={!settings.mapSettings.enabled}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography gutterBottom>{t('ecommerceContactSettings.map.zoomLevel')}</Typography>
              <Slider
                value={settings.mapSettings.zoom}
                onChange={(e, newValue) => updateMapSettings('zoom', newValue as number)}
                min={5}
                max={20}
                step={1}
                valueLabelDisplay="auto"
                disabled={!settings.mapSettings.enabled}
              />
            </Grid>

            {settings.mapSettings.latitude && settings.mapSettings.longitude && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <strong>{t('ecommerceContactSettings.map.selectedLocation')}</strong><br />
                  {t('ecommerceContactSettings.map.latitude')}: {settings.mapSettings.latitude.toFixed(6)}<br />
                  {t('ecommerceContactSettings.map.longitude')}: {settings.mapSettings.longitude.toFixed(6)}
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {t('ecommerceContactSettings.map.locationSelection')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('ecommerceContactSettings.map.locationSelectionHelp')}
              </Typography>
              <Box
                ref={mapRef}
                sx={{
                  height: 400,
                  width: `${settings.mapSettings.width}%`,
                  mx: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  opacity: settings.mapSettings.enabled ? 1 : 0.5,
                  pointerEvents: settings.mapSettings.enabled ? 'auto' : 'none',
                  position: 'relative',
                  zIndex: 0,
                  overflow: 'hidden',
                  isolation: 'isolate',
                  contain: 'paint',
                  '& .leaflet-container': {
                    height: '100%',
                    width: '100%',
                    borderRadius: 'inherit',
                    position: 'relative',
                    overflow: 'hidden',
                    zIndex: 0
                  }
                }}
              />
            </Grid>

            {settings.mapSettings.provider === 'google' &&
              settings.mapSettings.enabled &&
              settings.mapSettings.latitude != null &&
              settings.mapSettings.longitude != null && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Google Haritalar Önizlemesi
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Sitenizin iletişim sayfasında harita bu şekilde görünecek.
                  </Typography>
                  <Box
                    sx={{
                      height: settings.mapSettings.height,
                      width: `${settings.mapSettings.width}%`,
                      mx: 'auto',
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      overflow: 'hidden'
                    }}
                  >
                    <iframe
                      title="Google Haritalar Önizlemesi"
                      src={`https://maps.google.com/maps?q=${settings.mapSettings.latitude},${settings.mapSettings.longitude}&z=${settings.mapSettings.zoom}&hl=tr&output=embed`}
                      width="100%"
                      height="100%"
                      style={{ border: 0, display: 'block' }}
                      loading="lazy"
                      allowFullScreen
                    />
                  </Box>
                </Grid>
              )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      <FormPickerDialog
        open={formPickerOpen}
        configId={configId}
        onClose={() => setFormPickerOpen(false)}
        onSelect={({ slug, title }) => updateFormSelection(slug, title)}
      />
    </Paper>
  );
};

export default ContactSettingsComponent;
